// src/pages/transformation-rules/index.tsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
} from "lucide-react";
import api from "../api";

//  let TransformationRule = {
//   id: string;
//   ruleName: string;
//   ruleType: string;
//   description: string;
//   jsonLogic: Record<string, any>;
//   isActive: boolean;
//   created_by: number;
//   created_at: string;
//   updated_at: string;
// }

const defaultForm = {
  ruleName: "",
  ruleType: "",
  description: "",
  jsonLogic: {},
  isActive: true,
};

export default function TransformationRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] =
    useState(null);

  const [form, setForm] = useState(defaultForm);

  const fetchRules = async () => {
    try {
      setLoading(true);

      const res = await api.get("transformation-rules");

      setRules(res?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const filteredRules = rules?.filter((r) => {
    const keyword = search.toLowerCase();

    return (
      r.ruleName?.toLowerCase().includes(keyword) ||
      r.ruleType?.toLowerCase().includes(keyword)
    );
  });

  const openCreateModal = () => {
    setEditingRule(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);

    setForm({
      ...rule,
      jsonLogic: rule.jsonLogic,
    });

    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
      };

      console.log(`handleSave ${JSON.stringify(payload)}`)
      if (editingRule) {
        await api.put(
          `transformation-rules/${editingRule.id}`,
          payload
        );
      } else {
        await api.post("transformation-rules", payload);
      }

      setShowModal(false);
      fetchRules();
    } catch (err) {
      console.error(err);
      alert("Failed to save rule");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = 
      "Are you sure you want to delete this rule?"
    ;

    if (!confirmDelete) return;

    try {
      await api.delete(`transformation-rules/${id}`);

      fetchRules();
    } catch (err) {
      console.error(err);
      alert("Failed to delete");
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen p-6 font-display text-slate-900 min-h-screen flex flex-col">
      <main className="flex-1 px-6 md:px-10 py-8 max-w-[1440px] mx-auto w-full">

      {/* Header */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6 flex items-center justify-between">
            <div>
              <div className="text-xs font-black tracking-[0.3em] text-slate-400 uppercase">
                Reconciliation
              </div>

              <h1 className="mt-2 text-2xl font-black text-slate-900">
                 Transformation Rules
              </h1>

              <p className="mt-2 text-slate-500">
                Manage JSON Logic transformation rules.
              </p>
            </div>
            <div>
               <button
                onClick={openCreateModal}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 transition text-white px-4 py-2 rounded-lg"
              >
                <Plus size={18} />
                New Rule
              </button>
           </div>
        </div>

      {/* Toolbar */}
      <div className="bg-white border rounded-xl p-4 mb-5 flex items-center justify-between">
        <div className="relative w-[320px]">
          <Search
            className="absolute left-3 top-2.5 text-gray-400"
            size={18}
          />

          <input
            type="text"
            placeholder="Search rule..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded-lg pl-10 pr-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          onClick={fetchRules}
          className="flex items-center gap-2 border px-3 py-2 rounded-lg hover:bg-gray-100"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100 text-gray-700 text-sm">
            <tr>
              <th className="text-left px-4 py-3">Rule Name</th>
              <th className="text-left px-4 py-3">Rule Type</th>
              <th className="text-left px-4 py-3">Description</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Updated</th>
              <th className="text-center px-4 py-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-10 text-gray-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredRules.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-10 text-gray-400"
                >
                  No rules found
                </td>
              </tr>
            ) : (
              filteredRules.map((rule) => (
                <tr
                  key={rule.id}
                  className="border-t hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {rule.ruleName}
                  </td>

                  <td className="px-4 py-3">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                      {rule.ruleType || "-"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-600 max-w-[350px] truncate">
                    {rule.description || "-"}
                  </td>

                  <td className="px-4 py-3">
                    {rule.isActive ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle size={16} />
                        Active
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-red-500">
                        <XCircle size={16} />
                        Inactive
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(rule.updated_at).toLocaleString()}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => openEditModal(rule)}
                        className="p-2 rounded hover:bg-gray-100"
                      >
                        <Pencil size={16} />
                      </button>

                      <button
                        onClick={() => handleDelete(rule.id)}
                        className="p-2 rounded hover:bg-red-100 text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[760px] max-h-[90vh] overflow-auto shadow-2xl">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingRule
                  ? "Edit Transformation Rule"
                  : "Create Transformation Rule"}
              </h2>

              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-black"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Rule Name */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Rule Name
                </label>

                <input
                  type="text"
                  value={form.ruleName || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ruleName: e.target.value,
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* Rule Type */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Rule Type
                </label>

                <select
                  value={form.ruleType || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ruleType: e.target.value,
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select Type</option>
                  <option value="FORMAT">FORMAT</option>
                  <option value="MAPPING">MAPPING</option>
                  <option value="CALCULATION">CALCULATION</option>
                  <option value="NORMALIZATION">
                    NORMALIZATION
                  </option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Description
                </label>

                <textarea
                  rows={3}
                  value={form.description || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      description: e.target.value,
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* JSON Logic */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  JSON Logic
                </label>

                <textarea
                  rows={12}
                  value={JSON.stringify(
                    form.jsonLogic || {},
                    null,
                    2
                  )}
                  onChange={(e) => {
                    try {
                      setForm({
                        ...form,
                        jsonLogic: JSON.parse(e.target.value),
                      });
                    } catch {
                      // ignore parsing errors while typing
                    }
                  }}
                  className="w-full border rounded-lg px-3 py-2 font-mono text-sm"
                />
              </div>

              {/* Active */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.isActive || false}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      isActive: e.target.checked,
                    })
                  }
                />

                <label className="text-sm">
                  Active Rule
                </label>
              </div>
            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="border px-4 py-2 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg"
              >
                {editingRule ? "Update Rule" : "Create Rule"}
              </button>
            </div>
          </div>
        </div>
      )}
     </main>
    </div>
  );
}